import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';
import openHtmlInNewTab from '@/lib/openHtmlInNewTab';
// Phase 185.e — popup-blocker-proof signed view links
import openSignedDocInNewTab from '@/lib/openSignedDocInNewTab';
import { downloadDocument } from '@/lib/downloadDocument';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { RelationalSelect } from '@/components/RelationalSelect';
import { RelationalMultiSelect } from '@/components/RelationalMultiSelect';
import BulkOnboardModal from '@/components/onboarding/BulkOnboardModal';
import QuickResendCredentialsDialog from '@/components/onboarding/QuickResendCredentialsDialog';
// Phase 323 — Self-Signup QR + Batch Attendance QR
import SelfSignupQRDialog from '@/components/onboarding/SelfSignupQRDialog';
import AttendanceSessionQRDialog from '@/components/onboarding/AttendanceSessionQRDialog';
import {
  UserPlus, Search, MoreHorizontal, Edit, Trash2, Briefcase, IndianRupee,
  CalendarDays, CheckCircle, XCircle, Clock, Play, Ban, Key, UserCheck, UserX, Share2, TrendingUp, Users, Download, Shield,
  Sparkles, Zap, ChevronRight, Star, Plus, Trophy, Loader2, ClipboardCheck, FileSpreadsheet, Send, Mail, QrCode, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import { bestPhotoFor } from '@/lib/photo';
// Phase 281 — Context-aware Email Document dialog (payslip magic-link share)
import EmailDocumentDialog from '@/components/documents/EmailDocumentDialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { FALLBACK_PERMISSION_CATALOG } from '@/lib/permissions';
import { DocumentComposer } from '@/components/documents/DocumentComposer';
import { DocumentLedger } from '@/components/documents/DocumentLedger';
import { FileText as FileTextIcon } from 'lucide-react';
import { EmployeeDetailModal } from '@/components/EmployeeDetailModal';
// Phase 248 — Shared form hydrator (controlled/uncontrolled input safety net).
import { hydrateEmployeeForm } from '@/utils/formHydrators';

const formatDate = (d) => { if (!d) return '-'; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };

const roleOptions = ['Coach', 'Manager', 'Admin', 'Trainer', 'Receptionist', 'Maintenance'];
const deptOptions = ['Coaching', 'Administration', 'Finance', 'Operations', 'Marketing'];

// EmployeeForm defined OUTSIDE component to prevent re-mounting on state changes
const EmployeeForm = ({ data, setData, onSubmit, submitLabel }) => (
  <form onSubmit={onSubmit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} placeholder="Full name" data-testid="employee-name-input" required /></div>
      <div className="space-y-1.5"><Label className="text-xs">Email *</Label><Input type="email" value={data.email} onChange={(e) => setData({...data, email: e.target.value})} placeholder="email@example.com" required /></div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Password *</Label><Input type="password" value={data.password || ''} onChange={(e) => setData({...data, password: e.target.value})} placeholder="Min 6 characters" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={data.phone} onChange={(e) => setData({...data, phone: e.target.value})} /></div>
      <div className="space-y-1.5">
        <Label className="text-xs">Role</Label>
        <Select value={data.role} onValueChange={(v) => setData((prev) => ({...prev, role: v}))}>
          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{roleOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Department</Label>
        <RelationalSelect
          value={data.department_id || ''}
          onValueChange={(v) => setData((prev) => ({...prev, department_id: v}))}
          endpoint="/academy/departments"
          entityLabel="Department"
          placeholder="Select department"
          emptyHint="No departments configured — quick-add below."
          testid="employee-department-select"
        />
      </div>
      <div className="space-y-1.5"><Label className="text-xs">Designation</Label><Input value={data.designation} onChange={(e) => setData({...data, designation: e.target.value})} placeholder="Head Coach" /></div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Assigned Centers</Label>
        <RelationalMultiSelect
          values={data.assigned_center_ids || []}
          onValuesChange={(arr) => setData((prev) => ({...prev, assigned_center_ids: arr}))}
          endpoint="/academy/centers"
          entityLabel="Center"
          placeholder="Select one or more centers"
          emptyHint="No centers configured — add one below."
          testid="employee-center-select"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Assigned Batches</Label>
        <RelationalMultiSelect
          values={data.assigned_batch_ids || []}
          onValuesChange={(arr) => setData((prev) => ({...prev, assigned_batch_ids: arr}))}
          endpoint="/academy/batches"
          entityLabel="Batch"
          placeholder="Select one or more batches"
          emptyHint="No batches configured — add one below."
          testid="employee-batch-select"
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Base Salary (₹)</Label><Input type="number" value={data.base_salary} onChange={(e) => setData({...data, base_salary: e.target.value})} placeholder="25000" data-testid="employee-salary-input" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Join Date</Label><Input type="date" value={data.join_date} onChange={(e) => setData({...data, join_date: e.target.value})} /></div>
    </div>
    {/* Phase 279 — Itemized salary defaults (auto-inherit into monthly payroll) */}
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 dark:bg-emerald-900/10 dark:border-emerald-900/30 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-emerald-700">Smart Payroll Defaults</Label>
        <span className="text-[10px] text-emerald-700/70">Auto-fills into every new monthly payroll · still editable per month</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Performance Incentive (₹)</Label>
          <Input type="number" value={data.performance_incentive || ''} onChange={(e) => setData({...data, performance_incentive: e.target.value})} placeholder="0" data-testid="employee-performance-incentive-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Festival Bonus (₹)</Label>
          <Input type="number" value={data.festival_bonus || ''} onChange={(e) => setData({...data, festival_bonus: e.target.value})} placeholder="0" data-testid="employee-festival-bonus-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">P.F. Deduction (₹)</Label>
          <Input type="number" value={data.pf_deduction || ''} onChange={(e) => setData({...data, pf_deduction: e.target.value})} placeholder="0" data-testid="employee-pf-deduction-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Other Deductions (₹)</Label>
          <Input type="number" value={data.other_deductions || ''} onChange={(e) => setData({...data, other_deductions: e.target.value})} placeholder="0" data-testid="employee-other-deductions-input" />
        </div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">PAN</Label><Input value={data.pan} onChange={(e) => setData({...data, pan: e.target.value})} placeholder="ABCDE1234F" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Aadhar</Label><Input value={data.aadhar} onChange={(e) => setData({...data, aadhar: e.target.value})} placeholder="1234 5678 9012" /></div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5"><Label className="text-xs">Bank Account</Label><Input value={data.bank_account} onChange={(e) => setData({...data, bank_account: e.target.value})} /></div>
      <div className="space-y-1.5"><Label className="text-xs">Bank IFSC</Label><Input value={data.bank_ifsc} onChange={(e) => setData({...data, bank_ifsc: e.target.value})} /></div>
    </div>
    <Button type="submit" className="w-full" data-testid="employee-form-submit">{submitLabel}</Button>
  </form>
);

const PayrollStatusBadge = ({ status }) => {
  const colors = { pending: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>{status}</span>;
};

const LeaveStatusBadge = ({ status }) => {
  const colors = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>{status}</span>;
};

export default function HRMS() {
  const [employees, setEmployees] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employees');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkOnboard, setShowBulkOnboard] = useState(false);
  // Phase 323 — Self-Signup QR + Attendance Session QR
  const [showSelfSignupQR, setShowSelfSignupQR] = useState(false);
  const [showAttendanceQR, setShowAttendanceQR] = useState(false);
  // Phase 256c — Quick resend credentials (per-row dropdown action)
  const [quickResendEmp, setQuickResendEmp] = useState(null);
  const [editEmployee, setEditEmployee] = useState(null);
  // Phase 88 — Employee 360 detail modal trigger
  const [viewEmployee, setViewEmployee] = useState(null);
  const [showPayrollRun, setShowPayrollRun] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showPermissions, setShowPermissions] = useState(null);
  // Phase 99 — Granular ACL. Stored as an ARRAY of permission keys (not legacy dict).
  const [permissions, setPermissions] = useState([]);
  // Server-driven catalog: { permissions: [...], labels: {...}, groups: {Group: [...]} }
  const [permissionCatalog, setPermissionCatalog] = useState(null);
  // Phase 101 — Document Lifecycle modals
  const [composeForEmp, setComposeForEmp] = useState(null);
  const [ledgerForEmp, setLedgerForEmp] = useState(null);
  const [myAcademy, setMyAcademy] = useState(null);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [resetPwEmp, setResetPwEmp] = useState(null);
  const [resetPwForm, setResetPwForm] = useState({ password: '', confirm: '' });
  const [newEmployee, setNewEmployee] = useState({
    name: '', email: '', phone: '', role: '', department_id: '', designation: '',
    base_salary: '', performance_incentive: '', festival_bonus: '', pf_deduction: '', other_deductions: '',
    join_date: '', pan: '', aadhar: '', bank_account: '', bank_ifsc: '',
    assigned_center_ids: [], assigned_batch_ids: []
  });
  const [payrollForm, setPayrollForm] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'CL', start_date: '', end_date: '', reason: '' });
  const [empAttendance, setEmpAttendance] = useState({});
  const [empAttDate, setEmpAttDate] = useState(new Date().toISOString().split('T')[0]);
  const [empAttMonth, setEmpAttMonth] = useState(new Date().toISOString().slice(0, 7));
  const [empAttFilter, setEmpAttFilter] = useState('');
  const [empAttSelected, setEmpAttSelected] = useState('');
  const [empCalendarData, setEmpCalendarData] = useState([]);
  const [showEmpImport, setShowEmpImport] = useState(false);
  const [empImportData, setEmpImportData] = useState('');

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await api.get('/employees', { params: { search: search || undefined } });
      setEmployees(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [search]);

  const fetchPayroll = useCallback(async () => {
    try { const res = await api.get('/payroll'); setPayroll(res.data); } catch (err) { console.error(err); }
  }, []);

  const fetchLeaves = useCallback(async () => {
    try { const res = await api.get('/leaves'); setLeaves(res.data); } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  // Phase 101 — Cache academy for letterhead header
  useEffect(() => {
    api.get('/academy').then(r => setMyAcademy(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (activeTab === 'payroll') fetchPayroll(); }, [activeTab, fetchPayroll]);
  useEffect(() => { if (activeTab === 'leaves') fetchLeaves(); }, [activeTab, fetchLeaves]);

  const fetchEmpAttendance = useCallback(async () => {
    try {
      const res = await api.get('/employee-attendance', { params: { date: empAttDate } });
      const records = {};
      res.data.forEach(r => { records[r.employee_id] = r.status; });
      setEmpAttendance(records);
    } catch (err) { console.error(err); }
  }, [empAttDate]);

  useEffect(() => {
    if (activeTab === 'emp-attendance') fetchEmpAttendance();
    if (activeTab === 'emp-attendance' && empAttSelected && empAttMonth) {
      api.get('/employee-attendance', { params: { employee_id: empAttSelected, month: empAttMonth } })
        .then(r => setEmpCalendarData(r.data || []))
        .catch(() => setEmpCalendarData([]));
    }
  }, [activeTab, empAttDate, fetchEmpAttendance, empAttSelected, empAttMonth]);

  const handleMarkEmpAttendance = async (empId, status) => {
    try {
      await api.post('/employee-attendance', { employee_id: empId, date: empAttDate, status });
      setEmpAttendance(prev => ({ ...prev, [empId]: status }));
      toast.success(`Marked ${status}`);
    } catch (err) { apiErrorToast(err, 'Failed to mark attendance'); }
  };

  const handleBulkEmpAttendance = async (status) => {
    const records = employees.filter(e => e.status === 'active').map(e => ({ employee_id: e.id, status }));
    try {
      await api.post('/employee-attendance/bulk', { date: empAttDate, records });
      const newRecords = {};
      records.forEach(r => { newRecords[r.employee_id] = r.status; });
      setEmpAttendance(newRecords);
      toast.success(`All marked as ${status}`);
    } catch (err) { apiErrorToast(err, 'Failed to bulk-mark attendance'); }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      // Phase 279 — coerce all itemized salary defaults to numbers before posting.
      const payload = {
        ...newEmployee,
        base_salary: parseFloat(newEmployee.base_salary) || 0,
        performance_incentive: parseFloat(newEmployee.performance_incentive) || 0,
        festival_bonus: parseFloat(newEmployee.festival_bonus) || 0,
        pf_deduction: parseFloat(newEmployee.pf_deduction) || 0,
        other_deductions: parseFloat(newEmployee.other_deductions) || 0,
      };
      const res = await api.post('/employees', payload);
      const emp = res.data;
      if (emp.login_email && emp.login_password) {
        toast.success(`Employee added! Login: ${emp.login_email} / ${emp.login_password} (${emp.portal_role || 'employee'})`, { duration: 10000 });
      } else {
        toast.success('Employee added');
      }
      setShowAddDialog(false);
      setNewEmployee({ name: '', email: '', phone: '', role: '', department_id: '', designation: '', base_salary: '', performance_incentive: '', festival_bonus: '', pf_deduction: '', other_deductions: '', join_date: '', pan: '', aadhar: '', bank_account: '', bank_ifsc: '', assigned_center_ids: [], assigned_batch_ids: [] });
      fetchEmployees();
    } catch (err) {
      // Phase 286.d — Email-typo 422 recovery: prompt "Did you mean X?"
      const detail = err?.response?.data?.detail;
      if (
        err?.response?.status === 422 &&
        detail && typeof detail === 'object' &&
        detail.code === 'email_typo_suggested'
      ) {
        const useSuggested = window.confirm(
          `Email looks like a typo.\n\n` +
          `${detail.message}\n\n` +
          `OK    → use "${detail.suggested}"\n` +
          `Cancel → keep "${detail.original}" anyway`
        );
        try {
          if (useSuggested) {
            const corrected = { ...payload, email: detail.suggested };
            await api.post('/employees', corrected);
            toast.success(`Employee added with corrected email: ${detail.suggested}`);
          } else {
            await api.post('/employees?force_email=true', payload);
            toast.warning(`Employee added with un-corrected email: ${detail.original}`);
          }
          setShowAddDialog(false);
          setNewEmployee({ name: '', email: '', phone: '', role: '', department_id: '', designation: '', base_salary: '', performance_incentive: '', festival_bonus: '', pf_deduction: '', other_deductions: '', join_date: '', pan: '', aadhar: '', bank_account: '', bank_ifsc: '', assigned_center_ids: [], assigned_batch_ids: [] });
          fetchEmployees();
          return;
        } catch (err2) {
          apiErrorToast(err2, 'Failed to add employee');
          return;
        }
      }
      apiErrorToast(err, 'Failed to add employee');
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    try {
      // Phase 279 — itemized salary defaults flow through on update too.
      const payload = {
        ...editEmployee,
        base_salary: parseFloat(editEmployee.base_salary) || 0,
        performance_incentive: parseFloat(editEmployee.performance_incentive) || 0,
        festival_bonus: parseFloat(editEmployee.festival_bonus) || 0,
        pf_deduction: parseFloat(editEmployee.pf_deduction) || 0,
        other_deductions: parseFloat(editEmployee.other_deductions) || 0,
      };
      await api.put(`/employees/${editEmployee.id}`, payload);
      toast.success('Employee updated');
      setEditEmployee(null);
      fetchEmployees();
    } catch (err) { apiErrorToast(err, 'Failed to update employee'); }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    try { await api.delete(`/employees/${id}`); toast.success('Deleted'); fetchEmployees(); }
    catch (err) { apiErrorToast(err, 'Failed to delete employee'); }
  };

  const handleOpenPermissions = async (emp) => {
    try {
      // Phase 99 — Fetch the canonical catalogue lazily once, then per-employee
      // permission grants.
      if (!permissionCatalog) {
        try {
          const catRes = await api.get('/permissions/catalog');
          setPermissionCatalog(catRes.data);
        } catch { setPermissionCatalog(FALLBACK_PERMISSION_CATALOG); }
      }
      const res = await api.get(`/employees/${emp.id}/permissions`);
      // Backend returns { permissions: [], has_portal_account: bool }
      setPermissions(Array.isArray(res.data?.permissions) ? res.data.permissions : []);
      setShowPermissions({ ...emp, has_portal_account: res.data?.has_portal_account !== false });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to load permissions'); }
  };

  const handleSavePermissions = async () => {
    if (!showPermissions) return;
    try {
      // Phase 99 — Send permissions as a string array (canonical keys).
      const res = await api.put(`/employees/${showPermissions.id}/permissions`, { permissions });
      toast.success(`Permissions updated for ${showPermissions.name}`);
      setPermissions(res.data?.permissions || permissions);
      setShowPermissions(null);
      // Also tell every open tab to refresh /auth/me — if the affected user is
      // signed in elsewhere, their sidebar will reshape on next mutation.
      window.dispatchEvent(new Event('outplay-user-refresh'));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save permissions'); }
  };

  // Phase 202.c — Apply an Asana-style Role Template (Operations Manager,
  // Sports Director, Marketing Lead, Finance Officer, Custom). The owner can
  // still toggle individual scopes after the template is applied.
  const handleApplyTemplate = async (templateKey) => {
    if (!showPermissions || !templateKey || templateKey === 'custom') return;
    try {
      const res = await api.post(`/employees/${showPermissions.id}/permissions/apply-template`, { template: templateKey });
      setPermissions(res.data?.permissions || []);
      toast.success(`Applied template: ${res.data?.template_label}`);
      window.dispatchEvent(new Event('outplay-user-refresh'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply template');
    }
  };

  const handleToggleEmpStatus = async (emp) => {
    const newStatus = emp.status === 'active' ? 'inactive' : 'active';
    try { await api.put(`/employees/${emp.id}`, { status: newStatus }); toast.success(`Employee ${newStatus}`); fetchEmployees(); }
    catch (err) { apiErrorToast(err, 'Failed to toggle employee status'); }
  };

  const handleResetEmpPassword = async () => {
    if (!resetPwEmp) return;
    if (resetPwForm.password.length < 6) { toast.error('Min 6 characters'); return; }
    if (resetPwForm.password !== resetPwForm.confirm) { toast.error('Passwords do not match'); return; }
    try {
      const res = await api.post(`/employees/${resetPwEmp.id}/reset-password`, { password: resetPwForm.password });
      toast.success(res.data.message, { duration: 8000 });
      setResetPwEmp(null); setResetPwForm({ password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
  };

  // Computed filtered employees
  const filteredEmployees = employees.filter(e => {
    if (deptFilter && deptFilter !== 'all' && e.department !== deptFilter) return false;
    return true;
  });

  const handleExportEmployees = async () => {
    try {
      const res = await api.get('/export/employees.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.download = 'employees.csv'; link.click();
      window.URL.revokeObjectURL(url); toast.success('Employees exported!');
    } catch (err) { toast.error('Export failed'); }
  };

  const handleImportEmployees = async () => {
    try {
      const lines = empImportData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const emps = lines.slice(1).map(line => {
        const vals = line.split(','); const obj = {};
        headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); }); return obj;
      }).filter(e => e.name);
      let created = 0;
      for (const emp of emps) {
        try {
          await api.post('/employees', { name: emp.name, email: emp.email || '', phone: emp.phone || '', role: emp.role || 'Coach', department: emp.department || 'Coaching', base_salary: parseFloat(emp.salary || emp.base_salary || '0') });
          created++;
        } catch (e) {}
      }
      toast.success(`Imported ${created} employees`);
      setShowEmpImport(false); setEmpImportData(''); fetchEmployees();
    } catch (err) { toast.error('Import failed'); }
  };
  const totalPayroll = employees.reduce((s, e) => s + (e.base_salary || 0), 0);
  const activeCount = employees.filter(e => e.status === 'active').length;
  const coachCount = employees.filter(e => ['Coach', 'Trainer', 'coach', 'trainer'].includes(e.role)).length;

  const handleRunPayroll = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/payroll/run', payrollForm);
      toast.success(res.data.message);
      setShowPayrollRun(false);
      fetchPayroll();
    } catch (err) { toast.error('Failed to run payroll'); }
  };

  // Phase 279 — Itemized components in the payroll edit form.  Legacy `bonus`/
  // `deductions` are kept around so the row Bonus/Deductions columns can still
  // display the summed total in non-edit mode.
  const [payrollEditRow, setPayrollEditRow] = useState(null); // payroll id being edited
  // Phase 281 — Email-payslip dialog state.  Stores the payroll row that the
  // admin clicked "Email" on; cleared when the dialog closes.
  const [emailPayslipRow, setEmailPayslipRow] = useState(null);
  const [payrollEditForm, setPayrollEditForm] = useState({
    base_salary: '', performance_incentive: '', festival_bonus: '',
    pf_deduction: '', other_deductions: '', notes: '',
  });

  const handleStartEditPayroll = (p) => {
    setPayrollEditRow(p.id);
    // Phase 279 — populate itemized fields.  If a record predates Phase 279 (no
    // itemized fields on disk) seed Performance Incentive with the legacy
    // `bonus` value and P.F. Deduction with the legacy `deductions` value so the
    // first save migrates the data cleanly.
    const hasItemized = Number(p.performance_incentive || 0)
                      + Number(p.festival_bonus || 0)
                      + Number(p.pf_deduction || 0)
                      + Number(p.other_deductions || 0) > 0;
    setPayrollEditForm({
      base_salary: String(p.base_salary || 0),
      performance_incentive: String(hasItemized ? (p.performance_incentive || 0) : (p.bonus || 0)),
      festival_bonus: String(hasItemized ? (p.festival_bonus || 0) : 0),
      pf_deduction: String(hasItemized ? (p.pf_deduction || 0) : (p.deductions || 0)),
      other_deductions: String(hasItemized ? (p.other_deductions || 0) : 0),
      notes: p.notes || '',
    });
  };

  const handleSavePayrollEdit = async (id) => {
    try {
      await api.put(`/payroll/${id}`, {
        base_salary: parseFloat(payrollEditForm.base_salary) || 0,
        performance_incentive: parseFloat(payrollEditForm.performance_incentive) || 0,
        festival_bonus: parseFloat(payrollEditForm.festival_bonus) || 0,
        pf_deduction: parseFloat(payrollEditForm.pf_deduction) || 0,
        other_deductions: parseFloat(payrollEditForm.other_deductions) || 0,
        notes: payrollEditForm.notes || null,
      });
      toast.success('Payroll updated');
      setPayrollEditRow(null);
      fetchPayroll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  // Phase 279 — Live "Net Payout" preview during inline edit so the admin
  // sees the bottom-line as they type.  Proportional pay isn't recomputed on
  // the client (attendance lives on the row) — we just use the displayed
  // base & the live itemized components.
  const livePayrollEditNet = (rowProportional) => {
    const pi = parseFloat(payrollEditForm.performance_incentive) || 0;
    const fb = parseFloat(payrollEditForm.festival_bonus) || 0;
    const pf = parseFloat(payrollEditForm.pf_deduction) || 0;
    const od = parseFloat(payrollEditForm.other_deductions) || 0;
    return Math.round(((rowProportional || 0) + pi + fb - pf - od) * 100) / 100;
  };

  const handleRefreshPayroll = async () => {
    try {
      const res = await api.post('/payroll/run', payrollForm);
      toast.success(res.data.message);
      fetchPayroll();
    } catch (err) { apiErrorToast(err, 'Failed to refresh payroll'); }
  };

  const handleApprovePayroll = async (id) => {
    try { await api.put(`/payroll/${id}/approve`); toast.success('Payroll approved & synced to Finance'); fetchPayroll(); }
    catch (err) { apiErrorToast(err, 'Failed to approve payroll'); }
  };

  // ====== Phase 78 — Performance-Linked Bonuses ======
  const [perfSuggestions, setPerfSuggestions] = useState({}); // payroll_id -> suggestion
  const [perfModal, setPerfModal] = useState(null); // {payroll, suggestion}
  const [applyingPerf, setApplyingPerf] = useState(false);
  const fetchedPerfIds = useRef(new Set()); // avoid re-fetching the same id

  const fetchPerfSuggestion = useCallback(async (payroll_id) => {
    try {
      const res = await api.get(`/payroll/${payroll_id}/performance-suggestion`);
      setPerfSuggestions(prev => ({ ...prev, [payroll_id]: res.data }));
      return res.data;
    } catch (err) {
      return null;
    }
  }, []);

  // Prefetch suggestions whenever payroll list changes (only for pending records, once each)
  useEffect(() => {
    const pending = payroll.filter(p => p.status === 'pending');
    pending.forEach(p => {
      if (!fetchedPerfIds.current.has(p.id)) {
        fetchedPerfIds.current.add(p.id);
        fetchPerfSuggestion(p.id);
      }
    });
  }, [payroll, fetchPerfSuggestion]);

  const handleOpenPerfModal = async (p) => {
    let suggestion = perfSuggestions[p.id];
    if (!suggestion) suggestion = await fetchPerfSuggestion(p.id);
    if (!suggestion) {
      toast.error('Unable to load performance data');
      return;
    }
    setPerfModal({ payroll: p, suggestion });
  };

  const handleApplyPerfBonus = async ({ override_amount = null, append = false }) => {
    if (!perfModal) return;
    setApplyingPerf(true);
    try {
      const payload = { append };
      if (override_amount !== null) payload.override_amount = override_amount;
      await api.post(`/payroll/${perfModal.payroll.id}/apply-performance-bonus`, payload);
      toast.success('Performance bonus applied');
      setPerfModal(null);
      // Re-fetch BOTH payroll & suggestion so the UI reflects the new bonus
      await fetchPayroll();
      await fetchPerfSuggestion(perfModal.payroll.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplyingPerf(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leaves', leaveForm);
      toast.success('Leave applied');
      setShowLeaveDialog(false);
      setLeaveForm({ leave_type: 'CL', start_date: '', end_date: '', reason: '' });
      fetchLeaves();
    } catch (err) { apiErrorToast(err, 'Failed to apply leave'); }
  };

  const handleLeaveAction = async (id, action) => {
    try { await api.put(`/leaves/${id}/${action}`); toast.success(`Leave ${action}d`); fetchLeaves(); }
    catch (err) { apiErrorToast(err, `Failed to ${action} leave`); }
  };

  if (loading) return (
    <div className="space-y-4" data-testid="hrms-loading">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="hrms-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">HRMS</h1>
          <p className="text-sm text-muted-foreground">{employees.length} employees</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExportEmployees}><Download className="h-4 w-4 mr-1" /> Export</Button>
          <Dialog open={showEmpImport} onOpenChange={setShowEmpImport}>
            <DialogTrigger asChild><Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1 rotate-180" /> Import CSV</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Import Employees from CSV</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground"><p className="font-medium text-foreground mb-1">CSV Format:</p><code className="block bg-background p-2 rounded text-[10px]">name,email,phone,role,department,salary</code></div>
                <div className="space-y-1.5"><Label className="text-xs">Upload CSV File</Label><Input type="file" accept=".csv,.txt" className="text-xs" onChange={(e) => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = (ev) => setEmpImportData(ev.target.result); r.readAsText(f); } }} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Or paste CSV data</Label><textarea className="w-full h-24 text-xs font-mono border rounded-lg p-2 bg-muted/50 resize-none" placeholder={"name,email,phone,role,department,salary\nRavi Kumar,ravi@test.com,+91 9876543200,Coach,Coaching,25000"} value={empImportData} onChange={(e) => setEmpImportData(e.target.value)} /></div>
                {empImportData && <p className="text-xs text-muted-foreground">{empImportData.trim().split('\n').length - 1} employees to import</p>}
                <Button className="w-full" onClick={handleImportEmployees} disabled={!empImportData.trim()}>Import Employees</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="employees-add-button"><UserPlus className="h-4 w-4 mr-1" /> Add Employee</Button>
            </DialogTrigger>
            {/* Phase 280 — Cap modal height so the Smart Payroll Defaults section
                doesn't push the Save button off-screen on small viewports. */}
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
              <EmployeeForm data={newEmployee} setData={setNewEmployee} onSubmit={handleAddEmployee} submitLabel="Add Employee" />
            </DialogContent>
          </Dialog>
          {/* Phase 256 — Bulk Onboard Employees */}
          <Button size="sm" variant="outline" onClick={() => setShowBulkOnboard(true)} data-testid="employees-bulk-onboard-button">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Bulk Import
          </Button>
          {/* Phase 323 — Self-Signup QR */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowSelfSignupQR(true)}
            data-testid="employees-invite-qr-button"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <QrCode className="h-4 w-4 mr-1" /> Invite via QR
          </Button>
          <SelfSignupQRDialog
            open={showSelfSignupQR}
            onClose={() => setShowSelfSignupQR(false)}
            role="employee"
          />
          <AttendanceSessionQRDialog
            open={showAttendanceQR}
            onClose={() => setShowAttendanceQR(false)}
            kind="employee"
            batches={['Morning','Afternoon','Evening','Full-Day']}
          />
          <BulkOnboardModal
            open={showBulkOnboard}
            onClose={() => setShowBulkOnboard(false)}
            role="employee"
            onComplete={() => fetchEmployees?.()}
          />
          {/* Phase 256c — Quick Resend Credentials dialog (per-row dropdown) */}
          <QuickResendCredentialsDialog
            open={!!quickResendEmp}
            onClose={() => setQuickResendEmp(null)}
            role="employee"
            user={quickResendEmp}
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="overflow-x-auto justify-start no-scrollbar w-full sm:w-auto bg-slate-100/70 p-1">
          <TabsTrigger
            value="employees"
            data-testid="tab-employees"
            className="shrink-0 data-[state=active]:bg-sky-100 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm data-[state=active]:border-sky-300 data-[state=active]:border text-slate-600 hover:text-sky-700 transition-colors"
          >
            <Briefcase className="h-4 w-4 mr-1" /> Employees
          </TabsTrigger>
          <TabsTrigger
            value="payroll"
            data-testid="tab-payroll"
            className="shrink-0 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-800 data-[state=active]:shadow-sm data-[state=active]:border-amber-300 data-[state=active]:border text-slate-600 hover:text-amber-700 transition-colors"
          >
            <IndianRupee className="h-4 w-4 mr-1" /> Payroll
          </TabsTrigger>
          <TabsTrigger
            value="leaves"
            data-testid="tab-leaves"
            className="shrink-0 data-[state=active]:bg-rose-100 data-[state=active]:text-rose-800 data-[state=active]:shadow-sm data-[state=active]:border-rose-300 data-[state=active]:border text-slate-600 hover:text-rose-700 transition-colors"
          >
            <CalendarDays className="h-4 w-4 mr-1" /> Leaves
          </TabsTrigger>
          <TabsTrigger
            value="emp-attendance"
            data-testid="tab-emp-attendance"
            className="shrink-0 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm data-[state=active]:border-emerald-300 data-[state=active]:border text-slate-600 hover:text-emerald-700 transition-colors"
          >
            <CalendarDays className="h-4 w-4 mr-1" /> Attendance
          </TabsTrigger>
          <TabsTrigger
            value="reviews"
            data-testid="tab-hrms-reviews"
            className="shrink-0 data-[state=active]:bg-violet-100 data-[state=active]:text-violet-800 data-[state=active]:shadow-sm data-[state=active]:border-violet-300 data-[state=active]:border text-slate-600 hover:text-violet-700 transition-colors"
          >
            <Star className="h-4 w-4 mr-1" /> Reviews
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees">
          {/* AI Insight Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200"><CardContent className="p-3"><div className="flex items-center justify-between mb-1"><Users className="h-4 w-4 text-emerald-600" /><Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">Active</Badge></div><p className="text-xl font-display font-bold tabular-nums text-emerald-700">{activeCount}</p><p className="text-[10px] text-emerald-600 font-medium">Employees</p></CardContent></Card>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200"><CardContent className="p-3"><div className="flex items-center justify-between mb-1"><Briefcase className="h-4 w-4 text-blue-600" /><Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-700 border-blue-300">{Math.round(coachCount/Math.max(employees.length,1)*100)}%</Badge></div><p className="text-xl font-display font-bold tabular-nums text-blue-700">{coachCount}</p><p className="text-[10px] text-blue-600 font-medium">Coaches</p></CardContent></Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200"><CardContent className="p-3"><div className="flex items-center justify-between mb-1"><IndianRupee className="h-4 w-4 text-amber-600" /><Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-300">Monthly</Badge></div><p className="text-xl font-display font-bold tabular-nums text-amber-700">₹{(totalPayroll/1000).toFixed(0)}K</p><p className="text-[10px] text-amber-600 font-medium">Payroll</p></CardContent></Card>
            <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200"><CardContent className="p-3"><div className="flex items-center justify-between mb-1"><TrendingUp className="h-4 w-4 text-purple-600" /></div><p className="text-xl font-display font-bold tabular-nums text-purple-700">₹{employees.length > 0 ? (totalPayroll/employees.length/1000).toFixed(1) : 0}K</p><p className="text-[10px] text-purple-600 font-medium">Avg Salary</p></CardContent></Card>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 p-3 border-b border-border flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search by name, role, phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="employees-search-input" />
                </div>
                <Select value={deptFilter} onValueChange={setDeptFilter}>
                  <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="All Departments" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Departments</SelectItem>{deptOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="whitespace-nowrap">Role</TableHead>
                    <TableHead className="whitespace-nowrap">Department</TableHead>
                    <TableHead className="whitespace-nowrap">Salary</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No employees found.</TableCell></TableRow>
                  ) : filteredEmployees.map((emp) => (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`} className="cursor-pointer hover:bg-muted/40" onClick={() => setViewEmployee(emp)}>
                      <TableCell><span className="text-xs font-mono text-muted-foreground">{emp.custom_id || '-'}</span></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <StaffAvatar emp={emp} size={32} />
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{emp.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{emp.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs whitespace-nowrap">{emp.role}</Badge></TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{emp.department || '-'}</TableCell>
                      <TableCell className="font-semibold tabular-nums whitespace-nowrap">₹{(emp.base_salary || 0).toLocaleString()}</TableCell>
                      <TableCell className="whitespace-nowrap"><Badge variant={emp.status === 'active' ? 'default' : 'secondary'} className="text-xs">{emp.status}</Badge></TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setViewEmployee(emp)} data-testid={`view-employee-${emp.id}`}><Users className="h-4 w-4 mr-2" /> View Details</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditEmployee(hydrateEmployeeForm(emp))} data-testid={`edit-employee-${emp.id}`}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setResetPwEmp(emp)}><Key className="h-4 w-4 mr-2" /> Reset Password</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setQuickResendEmp(emp)} data-testid={`resend-credentials-${emp.id}`}><Send className="h-4 w-4 mr-2" /> Resend Credentials</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenPermissions(emp)} data-testid="permissions-btn"><Shield className="h-4 w-4 mr-2" /> Permissions</DropdownMenuItem>
                            {/* Phase 101 — Document Lifecycle */}
                            <DropdownMenuItem onClick={() => setComposeForEmp(emp)} data-testid={`emp-compose-doc-${emp.id}`}><FileTextIcon className="h-4 w-4 mr-2" /> Issue Document…</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setLedgerForEmp(emp)} data-testid={`emp-ledger-${emp.id}`}><FileTextIcon className="h-4 w-4 mr-2 opacity-60" /> Issued Documents</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleToggleEmpStatus(emp)}>
                              {emp.status === 'active' ? <><UserX className="h-4 w-4 mr-2" /> Deactivate</> : <><UserCheck className="h-4 w-4 mr-2" /> Activate</>}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteEmployee(emp.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll">
          <div className="space-y-4">
            {/* Phase 279 — Active processing context banner.  Always visible so
                the admin never has to guess which month is on the editing canvas. */}
            <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 dark:border-emerald-900/40 p-3 md:p-4 flex items-center justify-between flex-wrap gap-2" data-testid="payroll-active-period-banner">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-emerald-600 flex items-center justify-center shadow-sm">
                  <Clock className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700/80">Processing Payroll</p>
                  <p className="text-base md:text-lg font-display font-bold text-emerald-800 dark:text-emerald-200 tabular-nums" data-testid="payroll-active-period-label">
                    {new Date(payrollForm.year, payrollForm.month - 1).toLocaleString('en-IN', { month: 'long' }).toUpperCase()} {payrollForm.year}
                  </p>
                </div>
              </div>
              <span className="text-[11px] text-emerald-700/70">
                {payroll.length > 0
                  ? `${payroll.length} ${payroll.length === 1 ? 'record' : 'records'} on canvas`
                  : 'No records yet — click Run Payroll to generate'}
              </span>
            </div>
            {/* Payroll AI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-emerald-700">{payroll.filter(p => p.status === 'paid').length}/{payroll.length}</p><p className="text-[10px] text-emerald-600 font-medium">Paid This Period</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-blue-700">₹{(payroll.reduce((s,p) => s + (p.net_salary || 0), 0) / 1000).toFixed(0)}K</p><p className="text-[10px] text-blue-600 font-medium">Total Disbursed</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-amber-700">₹{(payroll.reduce((s,p) => s + (p.bonus || 0), 0) / 1000).toFixed(0)}K</p><p className="text-[10px] text-amber-600 font-medium">Bonuses</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-purple-700">{payroll.filter(p => p.status === 'pending').length}</p><p className="text-[10px] text-purple-600 font-medium">Pending Approval</p></CardContent></Card>
            </div>
            {/* Phase 280 — Mobile-friendly stacking for the period/Run Payroll controls. */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 flex-wrap">
              <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-2">
                <Label className="text-xs text-muted-foreground">Period:</Label>
                <Select value={String(payrollForm.month)} onValueChange={(v) => setPayrollForm({...payrollForm, month: parseInt(v)})}>
                  <SelectTrigger className="h-9 w-full sm:w-32 text-xs" data-testid="payroll-period-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 12}, (_, i) => <SelectItem key={i+1} value={String(i+1)}>{new Date(2024, i).toLocaleString('default', {month: 'long'})}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" className="h-9 w-full sm:w-24 text-xs" value={payrollForm.year} onChange={(e) => setPayrollForm({...payrollForm, year: parseInt(e.target.value)})} />
                <Button size="sm" variant="outline" className="h-9 text-xs w-full sm:w-auto" onClick={handleRefreshPayroll} data-testid="payroll-refresh-button">
                  <Clock className="h-3.5 w-3.5 mr-1" /> Refresh from Attendance
                </Button>
              </div>
              <Dialog open={showPayrollRun} onOpenChange={setShowPayrollRun}>
                <DialogTrigger asChild>
                  <Button size="sm" className="h-9 text-xs w-full sm:w-auto" data-testid="payroll-run-button"><Play className="h-4 w-4 mr-1" /> Run Payroll</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Run Payroll</DialogTitle></DialogHeader>
                  <form onSubmit={handleRunPayroll} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Month</Label>
                        <Select value={String(payrollForm.month)} onValueChange={(v) => setPayrollForm({...payrollForm, month: parseInt(v)})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 12}, (_, i) => <SelectItem key={i+1} value={String(i+1)}>{new Date(2024, i).toLocaleString('default', {month: 'long'})}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label className="text-xs">Year</Label><Input type="number" value={payrollForm.year} onChange={(e) => setPayrollForm({...payrollForm, year: parseInt(e.target.value)})} /></div>
                    </div>
                    <p className="text-xs text-muted-foreground p-2 bg-muted/50 rounded">
                      Regenerative payroll: refreshes attendance-based salary for pending records, preserving manual bonus/deductions. Approved (paid) records remain locked.
                    </p>
                    <Button type="submit" className="w-full" data-testid="payroll-run-submit">Generate / Refresh Payroll</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="whitespace-nowrap">Period</TableHead>
                      <TableHead className="whitespace-nowrap">Attendance</TableHead>
                      <TableHead className="whitespace-nowrap">Base</TableHead>
                      <TableHead className="whitespace-nowrap">Bonus</TableHead>
                      <TableHead className="whitespace-nowrap">Deductions</TableHead>
                      <TableHead className="whitespace-nowrap">Net</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-32"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payroll.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No payroll records. Click "Run Payroll" to generate.</TableCell></TableRow>
                    ) : payroll.map((p) => {
                      const isEditing = payrollEditRow === p.id;
                      const isPaid = p.status === 'paid';
                      return (
                        <TableRow key={p.id} data-testid={`payroll-row-${p.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <StaffAvatar emp={employees.find(e => e.id === p.employee_id) || { id: p.employee_id, name: p.employee_name }} size={28} />
                              <span className="font-medium text-sm">{p.employee_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">{p.month}/{p.year}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="text-xs tabular-nums">
                              <span className="text-emerald-600 font-semibold">{p.effective_days || p.days_in_month || 30}</span>
                              <span className="text-muted-foreground">/{p.days_in_month || 30} days</span>
                            </div>
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {isEditing ? (
                              <Input type="number" className="h-7 w-24 text-xs" value={payrollEditForm.base_salary} onChange={(e) => setPayrollEditForm({...payrollEditForm, base_salary: e.target.value})} data-testid={`payroll-base-input-${p.id}`} />
                            ) : `₹${(p.base_salary || 0).toLocaleString()}`}
                          </TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap">
                            {isEditing ? (
                              <div className="flex flex-col gap-1.5" data-testid={`payroll-bonus-stack-${p.id}`}>
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-[10px] text-emerald-700 w-20 shrink-0">Perf. Inc.</Label>
                                  <Input type="number" className="h-7 w-24 text-xs" value={payrollEditForm.performance_incentive} onChange={(e) => setPayrollEditForm({...payrollEditForm, performance_incentive: e.target.value})} data-testid={`payroll-perf-incentive-input-${p.id}`} />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-[10px] text-emerald-700 w-20 shrink-0">Festival</Label>
                                  <Input type="number" className="h-7 w-24 text-xs" value={payrollEditForm.festival_bonus} onChange={(e) => setPayrollEditForm({...payrollEditForm, festival_bonus: e.target.value})} data-testid={`payroll-festival-bonus-input-${p.id}`} />
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1">
                                <span>₹{(p.bonus || 0).toLocaleString()}</span>
                                {/* Phase 78 — Performance suggestion pill (visible for all pending rows for transparency) */}
                                {!isPaid && perfSuggestions[p.id] && (
                                  <button
                                    onClick={() => handleOpenPerfModal(p)}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border font-semibold transition-colors ${
                                      perfSuggestions[p.id].suggested_bonus > 0
                                        ? 'bg-gradient-to-r from-violet-100 to-fuchsia-100 hover:from-violet-200 hover:to-fuchsia-200 text-violet-700 border-violet-200'
                                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border-slate-200'
                                    }`}
                                    title="Click to review performance breakdown"
                                    data-testid={`perf-suggest-${p.id}`}
                                  >
                                    <Sparkles className="h-2.5 w-2.5" />
                                    {perfSuggestions[p.id].suggested_bonus > 0
                                      ? `Suggest ₹${perfSuggestions[p.id].suggested_bonus.toLocaleString()}`
                                      : 'No bonus due'}
                                  </button>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap">
                            {isEditing ? (
                              <div className="flex flex-col gap-1.5" data-testid={`payroll-deductions-stack-${p.id}`}>
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-[10px] text-red-700 w-20 shrink-0">P.F.</Label>
                                  <Input type="number" className="h-7 w-24 text-xs" value={payrollEditForm.pf_deduction} onChange={(e) => setPayrollEditForm({...payrollEditForm, pf_deduction: e.target.value})} data-testid={`payroll-pf-deduction-input-${p.id}`} />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-[10px] text-red-700 w-20 shrink-0">Other</Label>
                                  <Input type="number" className="h-7 w-24 text-xs" value={payrollEditForm.other_deductions} onChange={(e) => setPayrollEditForm({...payrollEditForm, other_deductions: e.target.value})} data-testid={`payroll-other-deductions-input-${p.id}`} />
                                </div>
                              </div>
                            ) : `₹${(p.deductions || 0).toLocaleString()}`}
                          </TableCell>
                          <TableCell className="font-semibold tabular-nums">
                            {isEditing ? (
                              <span className="text-emerald-700" data-testid={`payroll-net-preview-${p.id}`}>
                                ₹{livePayrollEditNet(p.proportional_salary || p.base_salary || 0).toLocaleString()}
                              </span>
                            ) : (
                              <>₹{(p.net_salary || 0).toLocaleString()}</>
                            )}
                          </TableCell>
                          <TableCell><PayrollStatusBadge status={p.status} /></TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {!isPaid && !isEditing && (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStartEditPayroll(p)} data-testid={`payroll-edit-${p.id}`}>
                                  <Edit className="h-3 w-3 mr-1" /> Edit
                                </Button>
                              )}
                              {!isPaid && isEditing && (
                                <>
                                  <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleSavePayrollEdit(p.id)} data-testid={`payroll-save-${p.id}`}>
                                    <CheckCircle className="h-3 w-3 mr-1" /> Save
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPayrollEditRow(null)}>Cancel</Button>
                                </>
                              )}
                              {!isPaid && !isEditing && (
                                <Button size="sm" variant="default" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprovePayroll(p.id)} data-testid="payroll-approve-button">
                                  <CheckCircle className="h-3 w-3 mr-1" /> Approve
                                </Button>
                              )}
                              {isPaid && (
                                <>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs inline-flex items-center whitespace-nowrap" onClick={() => openSignedDocInNewTab(
                                    `/payslips/${p.id}/view`,
                                    { label: 'Payslip', errorMessage: 'Failed to load' },
                                  )} data-testid="payslip-view">
                                    View
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs inline-flex items-center whitespace-nowrap" onClick={async () => {
                                    await downloadDocument(
                                      api,
                                      `/payslips/${p.id}/download`,
                                      `Payslip_${p.employee_name}_${p.month}_${p.year}.pdf`,
                                      { label: 'Payslip' }
                                    );
                                  }} data-testid="payslip-download">
                                    <Download className="h-3 w-3 mr-1" /> PDF
                                  </Button>
                                  {/* Phase 281 — Email magic-link to the employee. */}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs inline-flex items-center whitespace-nowrap"
                                    onClick={() => setEmailPayslipRow(p)}
                                    data-testid={`payslip-email-${p.id}`}
                                  >
                                    <Mail className="h-3 w-3 mr-1" /> Email
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Leaves Tab */}
        <TabsContent value="leaves">
          <div className="space-y-4">
            {/* Leaves AI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-blue-700">{leaves.filter(l => l.status === 'pending').length}</p><p className="text-[10px] text-blue-600 font-medium">Pending Approval</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-emerald-700">{leaves.filter(l => l.status === 'approved').length}</p><p className="text-[10px] text-emerald-600 font-medium">Approved</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-amber-700">{leaves.reduce((s,l) => s + (l.days || 1), 0)}</p><p className="text-[10px] text-amber-600 font-medium">Total Days Used</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-red-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-red-700">{leaves.filter(l => l.status === 'rejected').length}</p><p className="text-[10px] text-red-600 font-medium">Rejected</p></CardContent></Card>
            </div>
            <div className="flex justify-end">
              <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="leave-apply-button"><CalendarDays className="h-4 w-4 mr-1" /> Apply Leave</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Apply Leave</DialogTitle></DialogHeader>
                  <form onSubmit={handleApplyLeave} className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Leave Type</Label>
                      <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({...leaveForm, leave_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CL">Casual Leave</SelectItem>
                          <SelectItem value="SL">Sick Leave</SelectItem>
                          <SelectItem value="PL">Paid Leave</SelectItem>
                          <SelectItem value="LOP">Loss of Pay</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label className="text-xs">Start Date</Label><Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})} required /></div>
                      <div className="space-y-1.5"><Label className="text-xs">End Date</Label><Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})} required /></div>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">Reason</Label><Input value={leaveForm.reason} onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})} placeholder="Reason for leave" /></div>
                    <Button type="submit" className="w-full" data-testid="leave-apply-submit">Apply</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="whitespace-nowrap">Dates</TableHead>
                      <TableHead className="whitespace-nowrap">Days</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaves.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No leave requests.</TableCell></TableRow>
                    ) : leaves.map((l) => (
                      <TableRow key={l.id}>
                        <TableCell className="font-medium text-sm">{l.employee_name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{l.leave_type}</Badge></TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{l.start_date} to {l.end_date}</TableCell>
                        <TableCell className="tabular-nums whitespace-nowrap">{l.days}</TableCell>
                        <TableCell><LeaveStatusBadge status={l.status} /></TableCell>
                        <TableCell>
                          {l.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleLeaveAction(l.id, 'approve')}><CheckCircle className="h-3 w-3" /></Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs text-destructive" onClick={() => handleLeaveAction(l.id, 'reject')}><XCircle className="h-3 w-3" /></Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Employee Attendance Tab */}
        <TabsContent value="emp-attendance">
          <div className="space-y-4">
            {/* Attendance KPI Cards - SEPARATED METRICS */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200">
                <CardContent className="p-3">
                  <p className="text-xl font-display font-bold tabular-nums text-emerald-700">{Object.values(empAttendance).filter(s => s === 'present').length}</p>
                  <p className="text-[10px] text-emerald-600 font-medium">Present</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-red-200">
                <CardContent className="p-3">
                  <p className="text-xl font-display font-bold tabular-nums text-red-700">{Object.values(empAttendance).filter(s => s === 'absent').length}</p>
                  <p className="text-[10px] text-red-600 font-medium">Absent</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200">
                <CardContent className="p-3">
                  <p className="text-xl font-display font-bold tabular-nums text-amber-700">{Object.values(empAttendance).filter(s => s === 'late').length}</p>
                  <p className="text-[10px] text-amber-600 font-medium">Late</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/20 dark:to-gray-900/20 border-slate-200">
                <CardContent className="p-3">
                  <p className="text-xl font-display font-bold tabular-nums text-slate-700">{Object.values(empAttendance).filter(s => s === 'leave').length}</p>
                  <p className="text-[10px] text-slate-600 font-medium">Leave</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200">
                <CardContent className="p-3">
                  <p className="text-xl font-display font-bold tabular-nums text-purple-700">{Object.values(empAttendance).filter(s => s === 'half-day').length}</p>
                  <p className="text-[10px] text-purple-600 font-medium">Half-Day</p>
                </CardContent>
              </Card>
            </div>

            {/* Selectors Row */}
            <Card>
              <CardContent className="p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Input type="month" value={empAttMonth} onChange={(e) => setEmpAttMonth(e.target.value)} className="w-40 h-9 text-xs" />
                  <Select value={empAttSelected} onValueChange={setEmpAttSelected}>
                    <SelectTrigger className="w-52 h-9 text-xs"><SelectValue placeholder="Select Employee" /></SelectTrigger>
                    <SelectContent>{[...employees].filter(e => e.status === 'active').sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(e => <SelectItem key={e.id} value={e.id}>{e.custom_id} - {e.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={empAttFilter} onValueChange={setEmpAttFilter}>
                    <SelectTrigger className="w-44 h-9 text-xs"><SelectValue placeholder="All Departments" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Departments</SelectItem>{deptOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="ml-auto">
                    {/* Phase 323 — Open Attendance QR */}
                    <Button
                      size="sm"
                      onClick={() => setShowAttendanceQR(true)}
                      data-testid="employees-open-attendance-qr"
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <ScanLine className="h-4 w-4 mr-1" /> Open Attendance QR
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Employee Calendar View */}
            {empAttSelected && (() => {
              const selEmp = employees.find(e => e.id === empAttSelected);
              const year = parseInt(empAttMonth.split('-')[0]);
              const month = parseInt(empAttMonth.split('-')[1]) - 1;
              const daysInMonth = new Date(year, month + 1, 0).getDate();
              const firstDay = new Date(year, month, 1).getDay();
              const calMap = {};
              empCalendarData.forEach(r => { calMap[r.date] = r.status; });
              const present = Object.values(calMap).filter(s => s === 'present').length;
              const total = Object.keys(calMap).length;
              const rate = total > 0 ? Math.round(present / total * 100) : 0;
              const monthLabel = new Date(year, month).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
              return (
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">{selEmp?.name} — {monthLabel}</CardTitle>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Present</span>
                        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-500" /> Absent</span>
                        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500" /> Leave</span>
                        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-muted" /> No data</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex gap-2 text-sm flex-wrap">
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          {present} Present
                        </Badge>
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full bg-red-500" />
                          {Object.values(calMap).filter(s => s === 'absent').length} Absent
                        </Badge>
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full bg-amber-500" />
                          {Object.values(calMap).filter(s => s === 'late').length} Late
                        </Badge>
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full bg-slate-500" />
                          {Object.values(calMap).filter(s => s === 'leave').length} Leave
                        </Badge>
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <span className="h-2 w-2 rounded-full bg-purple-500" />
                          {Object.values(calMap).filter(s => s === 'half-day').length} Half-Day
                        </Badge>
                        <Badge className={`text-xs ${rate >= 85 ? 'bg-emerald-600' : rate >= 70 ? 'bg-amber-600' : 'bg-red-600'}`}>
                          {rate}% rate
                        </Badge>
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                          <div key={d} className="text-center text-[10px] font-medium text-muted-foreground py-1">{d}</div>
                        ))}
                        {Array.from({length: firstDay}).map((_, i) => <div key={`e${i}`} />)}
                        {Array.from({length: daysInMonth}).map((_, i) => {
                          const day = i + 1;
                          const dateStr = `${empAttMonth}-${String(day).padStart(2, '0')}`;
                          const status = calMap[dateStr];
                          const bg = status === 'present' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : status === 'absent' ? 'bg-red-100 text-red-800 border-red-300' : status === 'leave' ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-muted/30 text-muted-foreground border-transparent';
                          return (
                            <button key={day} onClick={() => handleMarkEmpAttendance(empAttSelected, status === 'present' ? 'absent' : 'present')} title={`${day}/${String(month+1).padStart(2,'0')}/${year}: ${status || 'no data'}`}
                              className={`h-9 rounded-md border text-xs font-medium flex items-center justify-center transition-colors duration-150 hover:ring-1 hover:ring-primary ${bg}`}>
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Quick Mark */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Quick Mark — {empAttDate.split('-').reverse().join('/')}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Input type="date" value={empAttDate} onChange={(e) => setEmpAttDate(e.target.value)} className="w-auto h-8 text-xs" />
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleBulkEmpAttendance('present')}><CheckCircle className="h-3.5 w-3.5 mr-1" /> All Present</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">ID</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead className="whitespace-nowrap">Role</TableHead>
                      <TableHead className="whitespace-nowrap">Dept</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.filter(e => e.status === 'active' && (!empAttFilter || empAttFilter === 'all' || e.department === empAttFilter)).map((emp) => (
                      <TableRow key={emp.id} className={empAttendance[emp.id] === 'present' ? 'animate-flash' : ''}>
                        <TableCell><span className="text-xs font-mono text-muted-foreground">{emp.custom_id || '-'}</span></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <StaffAvatar emp={emp} size={28} />
                            <span className="font-medium text-sm">{emp.name}</span>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline" className="text-xs whitespace-nowrap">{emp.role}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{emp.department || '-'}</TableCell>
                        <TableCell>
                          {empAttendance[emp.id] ? (
                            <Badge variant={empAttendance[emp.id] === 'present' ? 'default' : empAttendance[emp.id] === 'leave' ? 'secondary' : 'destructive'} className="text-xs">{empAttendance[emp.id]}</Badge>
                          ) : <span className="text-xs text-muted-foreground">Not marked</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant={empAttendance[emp.id] === 'present' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => handleMarkEmpAttendance(emp.id, 'present')}><CheckCircle className="h-3 w-3 mr-1" /> P</Button>
                            <Button size="sm" variant={empAttendance[emp.id] === 'absent' ? 'destructive' : 'outline'} className="h-7 text-xs" onClick={() => handleMarkEmpAttendance(emp.id, 'absent')}><XCircle className="h-3 w-3 mr-1" /> A</Button>
                            <Button size="sm" variant={empAttendance[emp.id] === 'leave' ? 'secondary' : 'outline'} className="h-7 text-xs" onClick={() => handleMarkEmpAttendance(emp.id, 'leave')}><Clock className="h-3 w-3 mr-1" /> L</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Phase 79 — Performance Reviews tab (Academy Owner) */}
        <TabsContent value="reviews">
          <PerformanceReviewsTab employees={employees} />
        </TabsContent>
      </Tabs>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwEmp} onOpenChange={() => { setResetPwEmp(null); setResetPwForm({ password: '', confirm: '' }); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Key className="h-5 w-5 text-primary" /> Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <p className="font-medium">{resetPwEmp?.name}</p>
              <p className="text-xs text-muted-foreground">{resetPwEmp?.custom_id} — {resetPwEmp?.email || 'No email'}</p>
            </div>
            <div className="space-y-2"><Label className="text-xs">New Password</Label><Input type="password" placeholder="Min 6 characters" value={resetPwForm.password} onChange={(e) => setResetPwForm({...resetPwForm, password: e.target.value})} /></div>
            <div className="space-y-2"><Label className="text-xs">Confirm Password</Label><Input type="password" placeholder="Re-enter" value={resetPwForm.confirm} onChange={(e) => setResetPwForm({...resetPwForm, confirm: e.target.value})} /></div>
            {resetPwForm.password && resetPwForm.confirm && resetPwForm.password !== resetPwForm.confirm && <p className="text-xs text-destructive">Passwords do not match</p>}
            <Button className="w-full" onClick={handleResetEmpPassword} disabled={!resetPwForm.password || resetPwForm.password !== resetPwForm.confirm}><Key className="h-4 w-4 mr-2" /> Reset & Share</Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => toast.success(resetPwEmp?.phone ? `WhatsApp sent to ${resetPwEmp.phone}` : 'No phone')}><Share2 className="h-3 w-3 mr-1" /> WhatsApp</Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => toast.success(resetPwEmp?.email ? `Email sent to ${resetPwEmp.email}` : 'No email')}><Share2 className="h-3 w-3 mr-1" /> Email</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={!!editEmployee} onOpenChange={() => setEditEmployee(null)}>
        {/* Phase 280 — Tall-form scroll guard. */}
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Employee</DialogTitle></DialogHeader>
          {editEmployee && <EmployeeForm data={editEmployee} setData={setEditEmployee} onSubmit={handleEditEmployee} submitLabel="Save Changes" />}
        </DialogContent>
      </Dialog>

      {/* Phase 281 — Context-aware payslip email magic-link dialog. */}
      <EmailDocumentDialog
        open={!!emailPayslipRow}
        onOpenChange={(o) => { if (!o) setEmailPayslipRow(null); }}
        documentType="payslip"
        docPath={emailPayslipRow ? `/payslips/${emailPayslipRow.id}/view` : null}
        defaultEmail={emailPayslipRow ? (employees.find(e => e.id === emailPayslipRow.employee_id)?.email || '') : ''}
        defaultName={emailPayslipRow?.employee_name || ''}
        contextData={emailPayslipRow ? {
          period: `${new Date(emailPayslipRow.year, emailPayslipRow.month - 1).toLocaleString('en-IN', { month: 'long' })} ${emailPayslipRow.year}`,
          amount: `₹${Number(emailPayslipRow.net_salary || 0).toLocaleString('en-IN')}`,
        } : {}}
      />

      {/* Permissions Dialog */}
      <Dialog open={!!showPermissions} onOpenChange={() => setShowPermissions(null)}>
        <DialogContent className="max-w-md" data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-primary" /> Feature Permissions</DialogTitle>
            <p className="text-xs text-muted-foreground">Grant granular feature access without changing the employee's primary role.</p>
          </DialogHeader>
          {showPermissions && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium text-sm">{showPermissions.name}</p>
                <p className="text-xs text-muted-foreground">{showPermissions.role} • {showPermissions.department || 'No department'}</p>
                {showPermissions.has_portal_account === false && (
                  <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-300">This employee has no portal account yet — reset their password first to activate permissions.</p>
                )}
              </div>
              {(() => {
                const catalog = permissionCatalog || FALLBACK_PERMISSION_CATALOG;
                const groups = catalog.groups || { 'All Permissions': catalog.permissions };
                const labels = catalog.labels || {};
                const templates = catalog.templates || [];
                const togglePerm = (key) => setPermissions(prev => prev.includes(key)
                  ? prev.filter(p => p !== key)
                  : [...prev, key]);
                return (
                  <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                    {/* Phase 202.c — Role Template selector (Asana-style preset). */}
                    {templates.length > 0 && (
                      <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">Role Template</p>
                        <p className="text-[11px] text-muted-foreground">
                          Quick-start with a preset bundle, then customize individual scopes below.
                        </p>
                        <div className="grid grid-cols-2 gap-1.5 pt-1">
                          {templates.filter(t => t.key !== 'custom').map(t => (
                            <button
                              key={t.key}
                              type="button"
                              disabled={showPermissions.has_portal_account === false}
                              onClick={() => handleApplyTemplate(t.key)}
                              className="text-left text-[11px] px-2 py-1.5 rounded border border-border/60 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`permission-template-${t.key}`}
                              title={t.description}
                            >
                              <span className="font-semibold block">{t.label}</span>
                              <span className="text-muted-foreground text-[10px]">{(t.permissions || []).length} scopes</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {Object.entries(groups).map(([group, keys]) => (
                      <div key={group}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{group}</p>
                        <div className="space-y-1.5 rounded-md border border-border/60 p-2">
                          {keys.map(key => (
                            <label key={key} className="flex items-center justify-between py-1 px-1 rounded hover:bg-muted/40 cursor-pointer">
                              <span className="text-sm">{labels[key] || key}</span>
                              <Checkbox
                                checked={permissions.includes(key)}
                                onCheckedChange={() => togglePerm(key)}
                                data-testid={`perm-${key}`}
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <Button
                className="w-full"
                onClick={handleSavePermissions}
                disabled={showPermissions.has_portal_account === false}
                data-testid="permissions-save-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" /> Save Permissions
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 101 — Document Composer + Ledger for Employees */}
      <DocumentComposer
        open={!!composeForEmp}
        onClose={() => setComposeForEmp(null)}
        targetType="employee"
        target={composeForEmp}
        academy={myAcademy}
        onSaved={() => setComposeForEmp(null)}
      />
      <DocumentLedger
        open={!!ledgerForEmp}
        onClose={() => setLedgerForEmp(null)}
        targetType="employee"
        target={ledgerForEmp}
        academy={myAcademy}
      />

      {/* Phase 78 — Performance-Linked Bonus Modal */}
      <Dialog open={!!perfModal} onOpenChange={(open) => { if (!open) setPerfModal(null); }}>
        <DialogContent className="max-w-lg" data-testid="perf-bonus-modal" aria-describedby="perf-bonus-desc">
          {perfModal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-violet-600" />
                  Performance Bonus — {perfModal.payroll.employee_name}
                </DialogTitle>
                <p id="perf-bonus-desc" className="text-xs text-muted-foreground">{perfModal.payroll.month}/{perfModal.payroll.year}</p>
              </DialogHeader>

              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {/* Headline suggested amount */}
                <div className="p-4 rounded-lg bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-950/30 dark:to-fuchsia-950/30 border border-violet-200">
                  <p className="text-xs uppercase tracking-wider text-violet-700 font-semibold">Suggested Performance Bonus</p>
                  <p className="text-3xl font-display font-bold tabular-nums text-violet-700 mt-1" data-testid="perf-suggested-amount">
                    ₹{perfModal.suggestion.suggested_bonus.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Current bonus on payroll: ₹{(perfModal.suggestion.current_bonus || 0).toLocaleString()}
                  </p>
                  {!perfModal.suggestion.is_coach_linked && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                      ⚠️ This employee has no linked user account — training session & progress card counts may be 0.
                    </p>
                  )}
                </div>

                {/* Breakdown */}
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Breakdown</p>
                  {perfModal.suggestion.breakdown.map((b) => (
                    <div key={b.key} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`perf-line-${b.key}`}>
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Zap className={`h-4 w-4 mt-0.5 shrink-0 ${b.amount > 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <div>
                          <p className="text-sm font-medium">{b.label}</p>
                          <p className="text-[11px] text-muted-foreground">{b.metric}</p>
                        </div>
                      </div>
                      <p className={`text-sm font-display font-semibold tabular-nums ${b.amount > 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                        ₹{b.amount.toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Override */}
                <div className="p-3 border rounded-lg bg-muted/30">
                  <p className="text-xs font-semibold mb-2">Custom override (optional)</p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Enter custom ₹ amount"
                      className="h-8 text-xs"
                      data-testid="perf-override-input"
                      value={perfModal.override || ''}
                      onChange={(e) => setPerfModal({ ...perfModal, override: e.target.value })}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!perfModal.override || applyingPerf}
                      onClick={() => handleApplyPerfBonus({ override_amount: parseFloat(perfModal.override) || 0 })}
                      data-testid="perf-override-apply"
                    >
                      Apply Override
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-2 border-t">
                <Button
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                  onClick={() => handleApplyPerfBonus({})}
                  disabled={applyingPerf || perfModal.suggestion.suggested_bonus === 0}
                  data-testid="perf-apply-suggested"
                >
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  Apply Suggested ₹{perfModal.suggestion.suggested_bonus.toLocaleString()}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleApplyPerfBonus({ append: true })}
                  disabled={applyingPerf || perfModal.suggestion.suggested_bonus === 0}
                  data-testid="perf-append-suggested"
                >
                  <ChevronRight className="h-3.5 w-3.5 mr-1" />
                  Add to existing bonus (₹{((perfModal.suggestion.current_bonus || 0) + perfModal.suggestion.suggested_bonus).toLocaleString()} total)
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 88 — Employee 360 detail modal (Profile / Attendance / Reviews) */}
      <EmployeeDetailModal
        emp={viewEmployee}
        open={!!viewEmployee}
        onClose={() => setViewEmployee(null)}
        onEmployeeUpdated={(updated) => {
          // Phase 88.1 — Hot-patch the row so the table avatar refreshes without a full refetch.
          setEmployees(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
          setViewEmployee(prev => prev && prev.id === updated.id ? { ...prev, ...updated } : prev);
        }}
      />
    </div>
  );
}


// Phase 79 — Performance Reviews tab (Academy Owner view)
function PerformanceReviewsTab({ employees }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [empFilter, setEmpFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    employee_id: '', quarter: 1, year: new Date().getFullYear(),
    player_growth_score: 7, training_consistency_score: 7,
    strengths: '', improvement_areas: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year: yearFilter };
      if (empFilter !== 'all') params.employee_id = empFilter;
      const r = await api.get('/performance-reviews', { params });
      setReviews(r.data || []);
    } catch { setReviews([]); } finally { setLoading(false); }
  }, [yearFilter, empFilter]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const openNew = () => {
    setEditing(null);
    setForm({
      employee_id: employees[0]?.id || '', quarter: Math.ceil((new Date().getMonth() + 1) / 3),
      year: new Date().getFullYear(), player_growth_score: 7, training_consistency_score: 7,
      strengths: '', improvement_areas: '', notes: '',
    });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      employee_id: r.employee_id, quarter: r.quarter, year: r.year,
      player_growth_score: r.player_growth_score,
      training_consistency_score: r.training_consistency_score,
      strengths: r.strengths || '', improvement_areas: r.improvement_areas || '', notes: r.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.employee_id) { toast.error('Pick an employee'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/performance-reviews/${editing.id}`, {
          player_growth_score: parseFloat(form.player_growth_score),
          training_consistency_score: parseFloat(form.training_consistency_score),
          strengths: form.strengths, improvement_areas: form.improvement_areas, notes: form.notes,
        });
        toast.success('Review updated');
      } else {
        await api.post('/performance-reviews', {
          ...form,
          quarter: parseInt(form.quarter, 10),
          year: parseInt(form.year, 10),
          player_growth_score: parseFloat(form.player_growth_score),
          training_consistency_score: parseFloat(form.training_consistency_score),
        });
        toast.success('Review published — employee notified');
      }
      setShowForm(false);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try { await api.delete(`/performance-reviews/${id}`); toast.success('Deleted'); fetchReviews(); }
    catch (err) { apiErrorToast(err, 'Failed to delete review'); }
  };

  const overall = (form.player_growth_score && form.training_consistency_score)
    ? ((parseFloat(form.player_growth_score) + parseFloat(form.training_consistency_score)) / 2).toFixed(2)
    : '—';

  return (
    <div className="space-y-3" data-testid="performance-reviews-tab">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1"><Label className="text-xs">Year</Label>
          <Select value={String(yearFilter)} onValueChange={v => setYearFilter(parseInt(v, 10))}>
            <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Employee</Label>
          <Select value={empFilter} onValueChange={setEmpFilter}>
            <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={openNew} className="ml-auto" data-testid="new-review-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Review
        </Button>
      </div>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-center text-xs text-muted-foreground">Loading reviews…</div>
        ) : reviews.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            <Star className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />
            No reviews for {yearFilter} yet. Publish quarterly reviews to keep coaches accountable.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-center">Quarter</TableHead>
                  <TableHead className="text-center">Player Growth</TableHead>
                  <TableHead className="text-center">Training Consistency</TableHead>
                  <TableHead className="text-center">Overall</TableHead>
                  <TableHead className="text-right w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map(r => (
                  <TableRow key={r.id} data-testid={`review-row-${r.id}`}>
                    <TableCell>
                      <p className="text-sm font-medium">{r.employee_name}</p>
                      <p className="text-[10px] text-muted-foreground">{r.reviewer_name && `reviewed by ${r.reviewer_name}`}</p>
                    </TableCell>
                    <TableCell className="text-center text-sm">Q{r.quarter} {r.year}</TableCell>
                    <TableCell className="text-center tabular-nums">{r.player_growth_score}/10</TableCell>
                    <TableCell className="text-center tabular-nums">{r.training_consistency_score}/10</TableCell>
                    <TableCell className="text-center">
                      <Badge className={`tabular-nums ${r.overall_score >= 8 ? 'bg-emerald-100 text-emerald-700' : r.overall_score >= 6 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'} border-0`}>
                        {r.overall_score}/10
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(r)} data-testid={`edit-review-${r.id}`}><Edit className="h-3 w-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(r.id)} data-testid={`delete-review-${r.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent></Card>

      {/* Review form Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) setShowForm(false); }}>
        <DialogContent className="max-w-lg" data-testid="review-form-dialog" aria-describedby="review-form-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-rose-600" /> {editing ? 'Edit' : 'New'} Performance Review</DialogTitle>
            <p id="review-form-desc" className="text-xs text-muted-foreground">Quarterly review based on Player Growth & Training Consistency. The reviewee is notified automatically.</p>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-3 space-y-1">
                <Label className="text-xs">Employee</Label>
                <Select value={form.employee_id} onValueChange={v => setForm({...form, employee_id: v})} disabled={!!editing}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Pick coach/employee" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Quarter</Label>
                <Select value={String(form.quarter)} onValueChange={v => setForm({...form, quarter: parseInt(v, 10)})} disabled={!!editing}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{[1,2,3,4].map(q => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Year</Label>
                <Input type="number" min="2020" max="2050" value={form.year} onChange={e => setForm({...form, year: parseInt(e.target.value, 10)})} className="h-9" disabled={!!editing} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><TrendingUp className="h-3 w-3 text-emerald-600" /> Player Growth (0–10)</Label>
                <Input type="number" step="0.5" min="0" max="10" value={form.player_growth_score} onChange={e => setForm({...form, player_growth_score: e.target.value})} className="h-9 text-lg font-bold tabular-nums" data-testid="player-growth-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><ClipboardCheck className="h-3 w-3 text-blue-600" /> Training Consistency (0–10)</Label>
                <Input type="number" step="0.5" min="0" max="10" value={form.training_consistency_score} onChange={e => setForm({...form, training_consistency_score: e.target.value})} className="h-9 text-lg font-bold tabular-nums" data-testid="training-consistency-input" />
              </div>
            </div>

            <div className="bg-rose-50 dark:bg-rose-950/20 rounded p-2 text-center" data-testid="review-overall-preview">
              <p className="text-[10px] uppercase text-rose-700">Overall Score</p>
              <p className="text-2xl font-display font-bold tabular-nums text-rose-700">{overall}/10</p>
            </div>

            <div className="space-y-1"><Label className="text-xs">Strengths</Label>
              <Input value={form.strengths} onChange={e => setForm({...form, strengths: e.target.value})} placeholder="e.g. excellent batch engagement, low absenteeism" /></div>
            <div className="space-y-1"><Label className="text-xs">Improvement areas</Label>
              <Input value={form.improvement_areas} onChange={e => setForm({...form, improvement_areas: e.target.value})} placeholder="e.g. punctuality, lesson planning" /></div>
            <div className="space-y-1"><Label className="text-xs">Notes (private)</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>

            <Button type="submit" disabled={saving} className="w-full" data-testid="save-review-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              {editing ? 'Update Review' : 'Publish Review'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Phase 79 — Compact staff avatar with photo or initials fallback
function StaffAvatar({ emp, size = 32 }) {
  const url = bestPhotoFor(emp);
  const initials = (emp?.name || '').split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase() || 'S';
  const px = `${size}px`;
  if (url) {
    return (
      <img
        src={url}
        alt={emp?.name || ''}
        className="rounded-full object-cover border border-border shrink-0"
        style={{ width: px, height: px }}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        data-testid={`staff-avatar-${emp?.id || 'x'}`}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center text-[10px] font-semibold shrink-0"
      style={{ width: px, height: px }}
      data-testid={`staff-avatar-${emp?.id || 'x'}`}
    >{initials}</div>
  );
}
